import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { customersAPI, jobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Edit2, 
    Trash2, 
    Phone, 
    Mail, 
    MapPin,
    FileText,
    Plus,
    Loader2,
    ChevronRight,
    PoundSterling
} from 'lucide-react';

const statusColors = {
    Draft: 'bg-slate-100 text-slate-700',
    Sent: 'bg-blue-100 text-blue-700',
    Accepted: 'bg-green-100 text-green-700',
    Completed: 'bg-emerald-100 text-emerald-700',
    Lost: 'bg-red-100 text-red-700'
};

export const CustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        notes: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postcode: ''
    });

    useEffect(() => {
        fetchCustomer();
        fetchCustomerJobs();
    }, [id]);

    const fetchCustomer = async () => {
        try {
            const response = await customersAPI.get(id);
            setCustomer(response.data);
            const addr = response.data.addresses?.[0] || {};
            setFormData({
                name: response.data.name,
                phone: response.data.phone,
                email: response.data.email,
                notes: response.data.notes,
                address_line1: addr.line1 || '',
                address_line2: addr.line2 || '',
                city: addr.city || '',
                postcode: addr.postcode || ''
            });
        } catch (error) {
            console.error('Failed to fetch customer:', error);
            toast.error('Customer not found');
            navigate('/customers');
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerJobs = async () => {
        try {
            const response = await jobsAPI.list({ customer_id: id });
            setJobs(response.data);
        } catch (error) {
            console.error('Failed to fetch jobs:', error);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Customer name is required');
            return;
        }

        setSaving(true);
        try {
            const response = await customersAPI.update(id, {
                name: formData.name,
                phone: formData.phone,
                email: formData.email,
                notes: formData.notes,
                addresses: [{
                    line1: formData.address_line1,
                    line2: formData.address_line2,
                    city: formData.city,
                    postcode: formData.postcode
                }]
            });
            setCustomer(response.data);
            setEditing(false);
            toast.success('Customer updated!');
        } catch (error) {
            console.error('Failed to update customer:', error);
            toast.error('Failed to update customer');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await customersAPI.delete(id);
            toast.success('Customer deleted');
            navigate('/customers');
        } catch (error) {
            console.error('Failed to delete customer:', error);
            toast.error('Failed to delete customer');
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-40 w-full rounded-lg" />
                    <Skeleton className="h-64 w-full rounded-lg" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/customers')}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            {customer?.name}
                        </h1>
                        <p className="text-slate-500 text-sm">{jobs.length} jobs</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setEditing(!editing)}
                            className="h-10 w-10"
                            data-testid="edit-customer-btn"
                        >
                            <Edit2 className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid="delete-customer-btn"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Customer?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will permanently delete {customer?.name}. This action cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={handleDelete}
                                        className="bg-red-600 hover:bg-red-700"
                                    >
                                        Delete
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>

                {/* Customer Details Card */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">
                            Customer Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {editing ? (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Name *
                                    </Label>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="h-12 border-slate-300"
                                        data-testid="edit-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Phone
                                    </Label>
                                    <Input
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="h-12 border-slate-300"
                                        data-testid="edit-phone-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Email
                                    </Label>
                                    <Input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="h-12 border-slate-300"
                                        data-testid="edit-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Notes
                                    </Label>
                                    <Textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={3}
                                        className="border-slate-300"
                                        data-testid="edit-notes-input"
                                    />
                                </div>
                                {/* Address Fields */}
                                <div className="pt-2 border-t border-slate-100">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Address</p>
                                    <div className="space-y-3">
                                        <Input
                                            name="address_line1"
                                            placeholder="Address Line 1"
                                            value={formData.address_line1}
                                            onChange={handleChange}
                                            className="h-11 border-slate-300"
                                            data-testid="edit-address-line1-input"
                                        />
                                        <Input
                                            name="address_line2"
                                            placeholder="Address Line 2"
                                            value={formData.address_line2}
                                            onChange={handleChange}
                                            className="h-11 border-slate-300"
                                            data-testid="edit-address-line2-input"
                                        />
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input
                                                name="city"
                                                placeholder="Town / City"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className="h-11 border-slate-300"
                                                data-testid="edit-city-input"
                                            />
                                            <Input
                                                name="postcode"
                                                placeholder="Postcode"
                                                value={formData.postcode}
                                                onChange={handleChange}
                                                className="h-11 border-slate-300 uppercase"
                                                data-testid="edit-postcode-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase"
                                        data-testid="save-changes-btn"
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Changes'
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setEditing(false)}
                                        className="h-12"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {customer?.phone && (
                                    <a 
                                        href={`tel:${customer.phone}`}
                                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <Phone className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase font-medium">Phone</p>
                                            <p className="font-semibold text-slate-900">{customer.phone}</p>
                                        </div>
                                    </a>
                                )}
                                {customer?.email && (
                                    <a 
                                        href={`mailto:${customer.email}`}
                                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                            <Mail className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase font-medium">Email</p>
                                            <p className="font-semibold text-slate-900">{customer.email}</p>
                                        </div>
                                    </a>
                                )}
                                {customer?.addresses?.[0]?.line1 && (
                                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                                            <Mail className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase font-medium">Address</p>
                                            <p className="font-semibold text-slate-900">{customer.addresses[0].line1}</p>
                                            {customer.addresses[0].line2 && <p className="text-slate-700 text-sm">{customer.addresses[0].line2}</p>}
                                            {(customer.addresses[0].city || customer.addresses[0].postcode) && (
                                                <p className="text-slate-700 text-sm">{customer.addresses[0].city} {customer.addresses[0].postcode}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {customer?.notes && (
                                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                                        <p className="text-xs text-amber-700 uppercase font-medium mb-1">Notes</p>
                                        <p className="text-slate-700">{customer.notes}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Jobs History */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight">
                                Job History
                            </CardTitle>
                            <Link to={`/jobs/new?customer=${id}`}>
                                <Button
                                    size="sm"
                                    className="h-9 bg-red-600 hover:bg-red-700 text-white"
                                    data-testid="new-job-for-customer-btn"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    New Job
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {jobs.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {jobs.map((job) => (
                                    <Link
                                        key={job.id}
                                        to={`/jobs/${job.id}`}
                                        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                        data-testid={`job-link-${job.quote_number}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <FileText className="h-5 w-5 text-slate-400" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-900">{job.quote_number}</p>
                                                <div className="flex items-center gap-2">
                                                    <Badge className={`${statusColors[job.status]} text-xs`}>
                                                        {job.status}
                                                    </Badge>
                                                    <span className="text-xs text-slate-500">
                                                        {formatDate(job.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-slate-900">
                                                {formatCurrency(job.pricing_summary?.total)}
                                            </span>
                                            <ChevronRight className="h-5 w-5 text-slate-400" />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FileText className="h-8 w-8 text-slate-400" />
                                </div>
                                <p className="text-slate-600 font-medium">No jobs yet</p>
                                <p className="text-slate-500 text-sm mt-1 mb-4">
                                    Create the first job for this customer
                                </p>
                                <Link to={`/jobs/new?customer=${id}`}>
                                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Start Survey
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};
